import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import './LiquidCursor.css';

/**
 * Single water bubble cursor — one glassy droplet that follows the mouse,
 * gently wobbles, and shifts to crimson on hover.
 */
export function LiquidCursor() {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const skinRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!bubbleRef.current || !skinRef.current) return;

    const setX = gsap.quickTo(bubbleRef.current, 'x', { duration: 0.22, ease: 'power3' });
    const setY = gsap.quickTo(bubbleRef.current, 'y', { duration: 0.22, ease: 'power3' });

    // Continuous gentle float wobble
    const wobble = gsap.timeline({ repeat: -1, yoyo: true })
      .fromTo(
        skinRef.current,
        { x: -3, y: -2 },
        { x: 3, y: 2, duration: 2.4, ease: 'sine.inOut' }
      );

    // Occasional pop — shrink + bounce back
    const popInterval = window.setInterval(() => {
      if (!skinRef.current) return;
      gsap.to(skinRef.current, {
        scale: 0.35,
        duration: 0.22,
        ease: 'power3.in',
        onComplete: () => {
          gsap.to(skinRef.current, { scale: 1, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
        },
      });
    }, 5500);

    const onMove = (e: MouseEvent) => {
      setX(e.clientX);
      setY(e.clientY);
    };

    const onOver = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const trigger = t.closest('a, button, [data-cursor="hover"]') as HTMLElement | null;
      if (trigger) {
        setHover(true);
        setLabel(trigger.getAttribute('data-cursor-label'));
      } else {
        setHover(false);
        setLabel(null);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseover', onOver);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      wobble.kill();
      window.clearInterval(popInterval);
    };
  }, []);

  return (
    <div className={`liquid-cursor ${hover ? 'liquid-cursor--hover' : ''}`} aria-hidden>
      <div ref={bubbleRef} className="liquid-cursor__bubble">
        <div ref={skinRef} className="liquid-cursor__skin">
          <span className="liquid-cursor__shine" />
        </div>
      </div>
      {label && <div className="liquid-cursor__label">{label}</div>}
    </div>
  );
}
